import type { ListParams, PageResult } from '@beonit/adaptive-raindrop';
import { Permission } from '@shared/infrastructure/auth/permission.js';
import { ApiError, ApiErrorCode } from '@shared/infrastructure/http/api-error.js';
import { useAuthStore } from '@shared/presentation/auth/store.js';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, type MaybeRef, onScopeDispose, type Ref, toValue, watch } from 'vue';
import { useRouter } from 'vue-router';
import { analysisConfigsApi, type SaveConfigInput } from './api/analysis-configs.api.js';
import { analysisDetailApi } from './api/analysis-detail.api.js';
import { analysisGroupsApi } from './api/analysis-groups.api.js';
import { analysisRunsApi } from './api/analysis-runs.api.js';
import { controlGroupsApi } from './api/control-groups.api.js';
import { detectionFeedbackApi } from './api/detection-feedback.api.js';
import { engineChangelogApi } from './api/engine-changelog.api.js';
import { executionVideosApi } from './api/execution-videos.api.js';
import { gestureLabelsApi } from './api/gesture-labels.api.js';
import { metricsApi } from './api/metrics.api.js';
import { preferencesApi, type UserPreferences } from './api/preferences.api.js';
import { videoLabellingApi } from './api/video-labelling.api.js';
import { videoTranscriptionsApi } from './api/video-transcriptions.api.js';
import { videosApi } from './api/videos.api.js';
import { isNoActiveConfig } from './lib/active-config.js';
import {
  type ConfigChange,
  diffConfigs,
  diffEngineVersions,
  type EngineChange,
} from './lib/config-diff.js';
import { deleteConfigsInTurn } from './lib/config-list.js';
import { executionResultLocation, launchWithEmail } from './lib/execution-email.js';
import { removeAll } from './lib/group-members-table.js';
import {
  controlOwner,
  groupOwner,
  ownerControlKind,
  ownerGroupId,
  ownerKey,
} from './lib/group-owner.js';
import type { IncoherenceRange } from './lib/incoherence-range.js';
import { mayHaveLaunched } from './lib/launch-options.js';
import { DEFAULT_SCORE_METHOD } from './lib/metrics-series.js';
import { DEFAULT_PLAYBACK_RATE } from './lib/playback-rate.js';
import { landingMayHavePromoted, runsThatLanded } from './lib/run-actions.js';
import { settleMany } from './lib/run-list.js';
import { videosAreAlive } from './lib/video-presentation.js';
import {
  DATASET_REFETCH_MS,
  groupExecutionsAreAlive,
  groupsAreAlive,
  HISTORY_REFETCH_MS,
  QUEUE_REFETCH_MS,
  queueIsAlive,
  REVIEW_IDLE_REFETCH_MS,
  refetchEvery,
  runsAreAlive,
  TRANSCRIPTION_REFETCH_MS,
  transcriptionIsAlive,
} from './live-refetch.js';
import type {
  AnalysisConfigDetail,
  AnalysisConfigSummary,
  AnalysisGroup,
  AnalysisMetadata,
  AnalysisQueue,
  AnalysisRun,
  CoherenceMetrics,
  CoherenceScope,
  ConfigExecution,
  ConfigVersion,
  ControlGroup,
  DatasetVersionDetail,
  DatasetVersionList,
  ControlKind,
  ControlLaunchParameters,
  ControlPendingVideo,
  CreateConfigFromBaseInput,
  CreateGroupDraft,
  DeleteImpact,
  DeleteVariant,
  DetectionFeedback,
  EngineChangelog,
  ExecutionSubscription,
  ExecutionVideos,
  FeedbackDraft,
  GestureAnalysis,
  GestureCatalogue,
  GestureControlMetrics,
  GestureLabelDraft,
  GestureLabelScope,
  GestureMatrix,
  GestureMatrixKind,
  GroupBatch,
  GroupExecutionList,
  GroupFilters,
  GroupLaunchParameters,
  GroupMember,
  GroupMembershipRef,
  GroupMetrics,
  GroupOwner,
  GroupWaitingExecution,
  LandmarkWindow,
  LaunchOptions,
  MetricsSnapshot,
  ReanalyzeOutcome,
  RulesetCatalogue,
  RunFilters,
  RunPlacement,
  RunStatus,
  StoredConfigVersion,
  Transcription,
  UpdateGroupDraft,
  Video,
  VideoFilters,
  VideoGestureLabels,
  VideoLabelling,
  VideoTranscription,
  VideoUpload,
} from './model.js';

/**
 * The four states a run can still move out of.
 *
 * Listed rather than derived from `TERMINAL_RUN_STATUSES` because this travels to Virtual
 * Profiler as a filter, and a filter built by subtraction would silently start asking for
 * a state that upstream added and this side has never seen.
 */
const LIVE_RUN_STATUSES: readonly RunStatus[] = [
  'QUEUED',
  'DISPATCHED',
  'RUNNING',
  'CANCEL_REQUESTED',
];

/**
 * Query keys of the module.
 *
 * Exported as constants and never written inline: an invalidation that misspells a
 * key does nothing at all, and nothing fails — the screen simply keeps showing what
 * it had. Naming them once is what makes that impossible.
 */
export const virtualProfilerQueryKey = ['virtual-profiler'] as const;
export const analysisQueueQueryKey = [...virtualProfilerQueryKey, 'queue'] as const;
export const analysisRunsQueryKey = [...virtualProfilerQueryKey, 'runs'] as const;
export const analysisDetailQueryKey = [...virtualProfilerQueryKey, 'analysis'] as const;
// Up here with its siblings: a replay of a group run invalidates the group's queries too.
export const groupsQueryKey = [...virtualProfilerQueryKey, 'groups'] as const;
// Up here too: deleting an execution invalidates the metrics its runs were counted in.
export const metricsQueryKey = [...virtualProfilerQueryKey, 'metrics'] as const;

/** The history is keyed by what was asked for, so two pages are two cache entries. */
export function analysisRunsPageKey(params: ListParams, filters: RunFilters): readonly unknown[] {
  return [...analysisRunsQueryKey, 'page', params, filters];
}

export function analysisRunQueryKey(runId: string): readonly unknown[] {
  return [...analysisRunsQueryKey, 'one', runId];
}

/**
 * Whether this session may read the module at all.
 *
 * Every query here is gated on it. Asking without the permission buys a 403 and a
 * retry, and the screen shows the error of a request it should never have made.
 */
function useCanRead(): Ref<boolean> {
  const auth = useAuthStore();

  return computed(() => auth.can(Permission.VIRTUAL_PROFILER_READ));
}

export function useAnalysisQueue() {
  const canRead = useCanRead();

  return useQuery<AnalysisQueue>({
    queryKey: analysisQueueQueryKey,
    queryFn: () => analysisRunsApi.queue(),
    enabled: canRead,
    // Asks again while anything can still move, and stops on its own once nothing
    // can. The screen never has to remember to turn it off. Since the queue screen
    // went live this interval is the net under the socket, not the mechanism — which
    // is why it is a minute and not ten seconds.
    refetchInterval: ({ state }) => refetchEvery(QUEUE_REFETCH_MS, queueIsAlive(state.data)),
    /**
     * Not the default by accident.
     *
     * vue-query already holds the interval while the document is hidden, so `false`
     * changes nothing today — but a paused interval is a requirement of this screen,
     * and a requirement that only holds because of somebody else's default is a
     * requirement nobody is keeping.
     */
    refetchIntervalInBackground: false,
    /**
     * The queue is the one screen where a cached answer is the wrong answer: it is
     * read to decide whether to cancel something. It is served fresh and then kept
     * fresh — by the socket's invalidations, and by the interval when there is no
     * socket. Zero also means an invalidation always refetches instead of being
     * satisfied by data that was considered fresh a second ago, which would make an
     * event arrive and change nothing.
     */
    staleTime: 0,
  });
}

/**
 * A page of the run history.
 *
 * `params` and `filters` are refs because the table owns them: it publishes new
 * ones as the operator pages, sorts and filters, and the key changes with them so
 * vue-query serves the page it already has and fetches the one it does not.
 */
export function useAnalysisRuns(params: MaybeRef<ListParams>, filters: MaybeRef<RunFilters> = {}) {
  const canRead = useCanRead();

  return useQuery<PageResult<AnalysisRun>>({
    queryKey: computed(() => analysisRunsPageKey(toValue(params), toValue(filters))),
    queryFn: () => analysisRunsApi.history(toValue(params), toValue(filters)),
    enabled: canRead,
    /**
     * Keeps the previous page on screen while the next one loads.
     *
     * Without it the table empties on every page change and the rows jump, which
     * reads as a broken table rather than as a loading one.
     */
    placeholderData: (previous) => previous,
    // Only while a run on *this* page can still change. A page of finished runs is
    // history and never asks again.
    refetchInterval: ({ state }) =>
      refetchEvery(HISTORY_REFETCH_MS, runsAreAlive(state.data?.items)),
    refetchIntervalInBackground: false,
  });
}

export function useAnalysisRun(runId: MaybeRef<string>) {
  const canRead = useCanRead();

  return useQuery<AnalysisRun>({
    queryKey: computed(() => analysisRunQueryKey(toValue(runId))),
    queryFn: () => analysisRunsApi.get(toValue(runId)),
    enabled: computed(() => canRead.value && Boolean(toValue(runId))),
  });
}

/**
 * Invalidates everything about runs.
 *
 * Every action on a run moves it between lanes, so the queue and the history both
 * change — and so does the run's own detail if it happens to be open. Invalidating
 * the module's run subtree covers the three without having to guess which page the
 * row was on, which is a guess that would be wrong as soon as the operator sorted
 * the table.
 */
function useInvalidateRuns(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey }),
      queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
    ]);
  };
}

/** Takes a run out of the queue before it starts. Never «borrar». */
export function useWithdrawRun() {
  const invalidate = useInvalidateRuns();

  return useMutation({
    mutationFn: (runId: string) => analysisRunsApi.withdraw(runId),
    onSuccess: invalidate,
  });
}

/**
 * Asks a running analysis to stop.
 *
 * It lands at the next checkpoint, so the run does not disappear: it moves to
 * «cancelación pedida» and stays there for a moment. The invalidation is what makes
 * that visible; without it the button would look like it did nothing.
 */
export function useCancelRun() {
  const invalidate = useInvalidateRuns();

  return useMutation({
    mutationFn: (runId: string) => analysisRunsApi.cancel(runId),
    onSuccess: invalidate,
  });
}

export function usePauseQueue() {
  const invalidate = useInvalidateRuns();

  return useMutation({
    mutationFn: () => analysisRunsApi.pauseQueue(),
    onSuccess: invalidate,
  });
}

export function useResumeQueue() {
  const invalidate = useInvalidateRuns();

  return useMutation({
    mutationFn: () => analysisRunsApi.resumeQueue(),
    onSuccess: invalidate,
  });
}

/** Moves a waiting run within its lane. Only the queue changes, but cheap to be sure. */
export function useMoveRun() {
  const invalidate = useInvalidateRuns();

  return useMutation({
    mutationFn: ({ runId, placement }: { runId: string; placement: RunPlacement }) =>
      analysisRunsApi.move(runId, placement),
    onSuccess: invalidate,
  });
}

/**
 * Every run the filters match, asked for once when somebody wants them all.
 *
 * A mutation rather than a query: it answers a click, not a screen, and caching it would hand a
 * later click the runs of a queue that has moved since. Refused past `ALL_RUNS_LIMIT`.
 */
export function useAllMatchingRuns() {
  return useMutation({
    mutationFn: (filters: RunFilters) => analysisRunsApi.allMatching(filters),
  });
}

/**
 * Takes several runs out of the queue in one call, and says how each went (2026-10-29).
 *
 * The queue and the history are invalidated once, at the end. Never throws — see `settleMany`.
 */
export function useWithdrawRuns() {
  const invalidate = useInvalidateRuns();

  return useMutation({
    mutationFn: (runIds: readonly string[]) => settleMany(runIds, analysisRunsApi.withdrawMany),
    onSettled: invalidate,
  });
}

/**
 * Asks several running analyses to stop in one call. Kept apart from `useWithdrawRuns` on
 * purpose: withdrawing and cancelling are different decisions and are never sent together.
 */
export function useCancelRuns() {
  const invalidate = useInvalidateRuns();

  return useMutation({
    mutationFn: (runIds: readonly string[]) => settleMany(runIds, analysisRunsApi.cancelMany),
    onSettled: invalidate,
  });
}

/** Where the run sits in an analysis key: after the module, `analysis`, the part and the video. */
export const RUN_SEGMENT = 4;

/**
 * The parts of a review that are about one run of one video, and go stale with it. Exported for
 * its spec: what is not listed here — the reference truth, the gesture labels — belongs to the
 * video and must survive a relaunch.
 */
export const ANALYSIS_PARTS = ['metadata', 'gestures', 'transcription', 'landmarks'] as const;

/**
 * Forgets what the review screen cached about these videos, without reloading what is on screen.
 *
 * `refetchType: 'none'` on purpose. A launch changes nothing a review shows until the run lands —
 * and refetching the header signs a new video URL, which reloads the player in the middle of
 * somebody's review. Marked stale, the next visit asks again; the run landing is handled by
 * `useRefreshWhenRunsLand`, which does reload, because by then there is something new to show.
 */
function useForgetVideoAnalyses(): (videoIds: readonly number[]) => Promise<void> {
  const queryClient = useQueryClient();

  return async (videoIds) => {
    await Promise.all(
      videoIds.flatMap((videoId) =>
        ANALYSIS_PARTS.map((part) =>
          queryClient.invalidateQueries({
            queryKey: [...analysisDetailQueryKey, part, videoId],
            refetchType: 'none',
          }),
        ),
      ),
    );
  };
}

// ---------------------------------------------------------------------------
// The video dataset (HU-15 to HU-23)
// ---------------------------------------------------------------------------

export const videosQueryKey = [...virtualProfilerQueryKey, 'videos'] as const;

export function videosPageKey(params: ListParams, filters: VideoFilters): readonly unknown[] {
  return [...videosQueryKey, 'page', params, filters];
}

export function videoQueryKey(videoId: number): readonly unknown[] {
  return [...videosQueryKey, 'one', videoId];
}

export function deleteImpactQueryKey(videoId: number): readonly unknown[] {
  return [...videosQueryKey, 'delete-impact', videoId];
}

/**
 * Runs of manual videos that have not finished.
 *
 * Its own query, and the reason is the verified trap of HU-23: **a group re-analysis never
 * changes the video's SQL row**. A gallery that read only the rows would show every video
 * as COMPLETED while a group's re-analysis of them runs, and would offer no way to see or
 * stop it.
 *
 * One request covers the whole page, whatever the page is — the card joins by source id.
 *
 * `idleEveryMs` keeps it asking while nothing is in flight, for a screen that has to notice a run
 * starting — the review. The gallery leaves it out and goes quiet.
 */
export function useLiveVideoRuns(options: { idleEveryMs?: number } = {}) {
  const canRead = useCanRead();

  return useQuery<PageResult<AnalysisRun>>({
    queryKey: [...videosQueryKey, 'live-runs'],
    queryFn: () =>
      analysisRunsApi.history(
        { page: 1, pageSize: 200 },
        { sourceType: 'manual_analysis', status: [...LIVE_RUN_STATUSES] },
      ),
    enabled: canRead,
    // Asks while anything came back, stops when nothing did. An empty answer means no
    // analysis is in flight, and that is exactly when this should go quiet.
    refetchInterval: ({ state }) =>
      refetchEvery(
        DATASET_REFETCH_MS,
        (state.data?.items.length ?? 0) > 0,
        options.idleEveryMs ?? false,
      ),
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

/**
 * A page of the gallery.
 *
 * The live runs are read here too, so the interval keeps going while a group re-analysis is
 * in flight even though every row on the page reads settled.
 */
export function useVideos(
  params: MaybeRef<ListParams>,
  filters: MaybeRef<VideoFilters> = {},
  /** Off, nothing is asked: a list only some screens need. */
  wanted: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();
  const live = useLiveVideoRuns();

  return useQuery<PageResult<Video>>({
    queryKey: computed(() => videosPageKey(toValue(params), toValue(filters))),
    queryFn: () => videosApi.list(toValue(params), toValue(filters)),
    enabled: computed(() => toValue(canRead) && toValue(wanted)),
    // Keeps the current page on screen while the next one loads, so the grid does not
    // empty and reflow on every page change.
    placeholderData: (previous) => previous,
    refetchInterval: ({ state }) =>
      refetchEvery(DATASET_REFETCH_MS, videosAreAlive(state.data?.items, live.data.value?.items)),
    refetchIntervalInBackground: false,
  });
}

/**
 * Every video the search and the filters match, asked for once when somebody wants them all.
 *
 * A mutation rather than a query: it answers a click, not a screen, and caching it would hand a
 * later click the videos of a gallery that has changed since.
 */
export function useAllMatchingVideos() {
  return useMutation({
    mutationFn: ({ search, filters }: { search: string | undefined; filters: VideoFilters }) =>
      videosApi.allMatching(search, filters),
  });
}

/**
 * The thumbnail of each video of a group, by video id.
 *
 * The members do not carry one; the gallery's rows do, and the gallery filters by group. Read once
 * and kept, not on the gallery's interval: every read signs new URLs, and a new URL is the same
 * image downloaded again. Twenty minutes is well inside the two hours a signed URL lasts. The
 * member ids are in the key, so a video added to the group brings its thumbnail with it.
 */
export function useGroupThumbnails(
  groupId: MaybeRef<number>,
  videoIds: MaybeRef<readonly number[]>,
) {
  const canRead = useCanRead();

  return useQuery<ReadonlyMap<number, string | null>>({
    queryKey: computed(() => [
      ...videosQueryKey,
      'group-thumbnails',
      toValue(groupId),
      [...toValue(videoIds)].sort((a, b) => a - b),
    ]),
    queryFn: async () => {
      const videos = await videosApi.allMatching(undefined, {
        groupId: toValue(groupId),
      });
      return new Map(videos.map((video) => [video.id, video.thumbnailUrl]));
    },
    enabled: computed(() => canRead.value && toValue(videoIds).length > 0),
    placeholderData: (previous) => previous,
    staleTime: 20 * 60_000,
  });
}

/**
 * One video's row.
 *
 * `fromGallery`: opened from the gallery, the row is already on screen there, and it stands in
 * while this one is read — so the detail opens on the name and the thumbnail somebody clicked, the
 * thumbnail under the very URL the browser has just loaded, which is what lets the player show it
 * at once. Only for showing: whatever decides something from the row (whether a launch is blocked,
 * which configuration it keeps) must wait for the row as it is now, not as the gallery last saw it.
 */
export function useVideo(videoId: MaybeRef<number>, options: { fromGallery?: boolean } = {}) {
  const canRead = useCanRead();
  const queryClient = useQueryClient();

  function galleryRow(): Video | undefined {
    const id = toValue(videoId);

    for (const [, page] of queryClient.getQueriesData<PageResult<Video>>({
      queryKey: [...videosQueryKey, 'page'],
    })) {
      const row = page?.items.find((video) => video.id === id);
      if (row) return row;
    }

    return undefined;
  }

  return useQuery<Video>({
    queryKey: computed(() => videoQueryKey(toValue(videoId))),
    queryFn: () => videosApi.get(toValue(videoId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0),
    placeholderData: options.fromGallery ? galleryRow : undefined,
  });
}

/**
 * What deleting this video would cost.
 *
 * Its own query so the dialog can read it before it opens, and enabled by the caller: it is
 * only worth asking when somebody is actually about to delete something.
 *
 * `staleTime: 0` because the answer depends on the groups and the snapshots of the moment,
 * and a cached impact would describe a state that has since changed.
 */
export function useDeleteImpact(videoId: MaybeRef<number | null>) {
  const canRead = useCanRead();

  return useQuery<DeleteImpact>({
    queryKey: computed(() => deleteImpactQueryKey(toValue(videoId) ?? 0)),
    queryFn: () => videosApi.deleteImpact(toValue(videoId) as number),
    enabled: computed(() => canRead.value && Boolean(toValue(videoId))),
    staleTime: 0,
  });
}

/** Invalidates the gallery: every page of it, plus whichever video's detail is open. */
function useInvalidateVideos(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({ queryKey: videosQueryKey });
  };
}

/**
 * What deleting or registering a video moves: the gallery, and the groups. A delete takes the video
 * out of every group it was in and an upload into a group of the funnel puts it in that one, so the
 * Dataset's sidebar beside the gallery — each group's count, «Reanalizar», the controls' pending
 * videos — would otherwise keep the numbers from before.
 */
export const VIDEO_SET_QUERY_KEYS = [videosQueryKey, groupsQueryKey] as const;

function useInvalidateVideoSet(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all(
      VIDEO_SET_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  };
}

/**
 * Renames a video.
 *
 * The answer is the video, so the cache is written straight rather than invalidated: the
 * card repaints with the new name without a second read, and a rename in a table of fifty
 * rows does not refetch the page.
 */
export function useRenameVideo() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateVideos();

  return useMutation({
    mutationFn: ({
      videoId,
      patch,
    }: {
      videoId: number;
      patch: { name?: string; description?: string | null };
    }) => videosApi.rename(videoId, patch),
    onSuccess: async (video) => {
      // The review header reads the name from this same entry, so it repaints with the card. The
      // analysis metadata is left alone: its name is upstream's original filename, not this one.
      queryClient.setQueryData(videoQueryKey(video.id), video);
      await invalidate();
    },
  });
}

/**
 * Marks or unmarks a video as a favourite.
 *
 * The answer is the video, so its cache entry is written straight and the card repaints without a
 * second read — same as the rename. The list is invalidated too, because a favourite changes the
 * order: it moves to the strip at the top.
 */
export function useSetVideoFavourite() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateVideos();

  return useMutation({
    mutationFn: ({ videoId, favourite }: { videoId: number; favourite: boolean }) =>
      videosApi.setFavourite(videoId, favourite),
    onSuccess: async (video) => {
      queryClient.setQueryData(videoQueryKey(video.id), video);
      await invalidate();
    },
  });
}

/**
 * Deletes a video the chosen way.
 *
 * The variant is part of the mutation's input with no default, all the way down. Three
 * variants, three consequences, and one of them destroys analyses.
 */
export function useDeleteVideo() {
  const invalidate = useInvalidateVideoSet();

  return useMutation({
    mutationFn: ({ videoId, variant }: { videoId: number; variant: DeleteVariant }) =>
      videosApi.remove(videoId, variant),
    onSuccess: invalidate,
  });
}

/**
 * Deletes several videos in one call (2026-10-29): each group they were in gets one dataset
 * version for all of them, not one per video. Read again even after a failure: some may be gone.
 */
export function useDeleteVideos() {
  const invalidate = useInvalidateVideoSet();

  return useMutation({
    mutationFn: (videoIds: readonly number[]) => videosApi.removeMany(videoIds),
    onSettled: invalidate,
  });
}

/**
 * How long re-analysing a selection would take.
 *
 * A mutation rather than a query, even though it reads nothing: it is asked for on demand,
 * for a selection that changes with every click, and caching an estimate per selection
 * would fill the cache with answers nobody asks twice.
 */
export function useEstimateVideos() {
  return useMutation({
    mutationFn: ({
      videoIds,
      engines,
    }: {
      videoIds: readonly number[];
      engines?: readonly string[];
    }) => videosApi.estimate(videoIds, engines ?? []),
  });
}

/**
 * Adds a video the browser has already uploaded to the dataset.
 *
 * Launches nothing (product owner, 2026-09-28), so only the gallery changes: the queue has nothing
 * new in it.
 */
export function useRegisterVideo() {
  const invalidate = useInvalidateVideoSet();

  return useMutation({
    mutationFn: (upload: VideoUpload) => videosApi.register(upload),
    onSuccess: invalidate,
  });
}

/**
 * What a background upload refreshes once a video is registered: the gallery, where the video
 * appears, and the groups, one of which it may have landed in. The same `useRegisterVideo`
 * refreshes, for an upload that registers from a store rather than a component.
 */
export function useRefreshAfterRegister(): () => Promise<void> {
  return useInvalidateVideoSet();
}

/**
 * Re-analyses one video, or a selection under one batch (GAP-03, GAP-15).
 *
 * One id goes to the video's own route and several to the collection's, because they answer
 * differently: one launch either happens or fails, while a batch always answers and lists the
 * videos it refused. The union keeps that difference in front of whoever reads the result.
 *
 * Invalidates the queue and the history, where the new runs are; the gallery, whose live runs and
 * configuration may have moved; and the cached reviews of these videos, without reloading one that
 * is open — nothing on it changes until the run lands. The same after a launch nobody confirmed,
 * which may be in the queue all the same.
 */
export function useReanalyzeVideos() {
  const invalidateRuns = useInvalidateRuns();
  const invalidateVideos = useInvalidateVideos();
  const forget = useForgetVideoAnalyses();
  const refresh = async (videoIds: readonly number[]) => {
    await Promise.all([invalidateRuns(), invalidateVideos(), forget(videoIds)]);
  };

  return useMutation({
    mutationFn: async ({
      videoIds,
      options,
    }: {
      videoIds: readonly number[];
      options: LaunchOptions;
    }): Promise<ReanalyzeOutcome> => {
      const [only] = videoIds;

      if (videoIds.length === 1 && only !== undefined) {
        return {
          kind: 'single',
          videoId: only,
          run: await videosApi.reanalyze(only, options),
        };
      }

      return {
        kind: 'bulk',
        videoIds: [...videoIds],
        launch: await videosApi.bulkReanalyze(videoIds, options),
      };
    },
    onSuccess: async (_outcome, { videoIds }) => {
      await refresh(videoIds);
    },
    onError: async (error, { videoIds }) => {
      if (mayHaveLaunched(error)) await refresh(videoIds);
    },
  });
}

/**
 * Launches a finished run again. Same engines, configuration and stored job as the original.
 *
 * `videoId` is the caller's because the answer does not carry it, and the run history of that
 * video is what changes.
 */
export function useReplayRun() {
  const queryClient = useQueryClient();
  const invalidateRuns = useInvalidateRuns();
  const forget = useForgetVideoAnalyses();
  const refresh = async (videoId: number | null) => {
    await Promise.all([
      invalidateRuns(),
      queryClient.invalidateQueries({ queryKey: videosQueryKey }),
      // A group run's replay lands in its execution: the history and the members change too.
      queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
      videoId === null ? Promise.resolve() : forget([videoId]),
    ]);
  };

  return useMutation({
    mutationFn: ({
      runId,
      batch,
      note,
    }: {
      runId: string;
      videoId: number | null;
      batch?: string;
      note?: string;
    }) => analysisRunsApi.replay(runId, { batch, note }),
    onSuccess: async (_replayed, { videoId }) => {
      await refresh(videoId);
    },
    onError: async (error, { videoId }) => {
      if (mayHaveLaunched(error)) await refresh(videoId);
    },
  });
}

export const analysisConfigsQueryKey = [...virtualProfilerQueryKey, 'analysis-configs'] as const;

/**
 * The analysis configurations.
 *
 * Cached for a while on purpose: they change when somebody edits one, which is rare, and
 * every upload dialog and every launch form reads them. Re-asking on each open would be a
 * request per dialog for an answer that has not moved.
 */
export function useAnalysisConfigs() {
  const canRead = useCanRead();

  return useQuery<AnalysisConfigSummary[]>({
    queryKey: analysisConfigsQueryKey,
    queryFn: () => analysisConfigsApi.list(),
    enabled: canRead,
    staleTime: 5 * 60_000,
  });
}

export function configDetailKey(configId: MaybeRef<number>): readonly unknown[] {
  return [...analysisConfigsQueryKey, 'detail', toValue(configId)];
}

export function configVersionsKey(configId: MaybeRef<number>): readonly unknown[] {
  return [...analysisConfigsQueryKey, 'versions', toValue(configId)];
}

/** Under the configurations' key, so every save, delete or activation refreshes it too. */
export const activeConfigQueryKey = [...analysisConfigsQueryKey, 'active'] as const;

export const gestureCatalogueQueryKey = [...virtualProfilerQueryKey, 'gesture-catalogue'] as const;

export const rulesetCatalogueQueryKey = [...virtualProfilerQueryKey, 'rulesets'] as const;

export function useAnalysisConfig(configId: MaybeRef<number>) {
  const canRead = useCanRead();

  return useQuery<AnalysisConfigDetail>({
    queryKey: computed(() => configDetailKey(configId)),
    queryFn: () => analysisConfigsApi.get(toValue(configId)),
    enabled: computed(() => canRead.value && toValue(configId) > 0),
  });
}

/**
 * The active configuration, the one «Configuración» opens on.
 *
 * Its answer is a whole configuration, so it is also written into that configuration's detail
 * cache: the screen it leads to paints at once instead of asking again for what it was just handed.
 * «None is active» is an answer, not a hiccup, and is not retried.
 *
 * Never fresh: somebody may have moved the star from another session, and opening the screen on
 * the configuration that had it half a minute ago would show as Beon's one it no longer uses.
 */
export function useActiveAnalysisConfig() {
  const canRead = useCanRead();
  const queryClient = useQueryClient();

  return useQuery<AnalysisConfigDetail>({
    queryKey: activeConfigQueryKey,
    queryFn: async () => {
      const active = await analysisConfigsApi.active();

      queryClient.setQueryData(configDetailKey(active.id), active);

      return active;
    },
    enabled: canRead,
    staleTime: 0,
    retry: (failures, error) => !isNoActiveConfig(error) && failures < 1,
  });
}

export function useConfigVersions(configId: MaybeRef<number>) {
  const canRead = useCanRead();

  return useQuery<ConfigVersion[]>({
    queryKey: computed(() => configVersionsKey(configId)),
    queryFn: () => analysisConfigsApi.versions(toValue(configId)),
    enabled: computed(() => canRead.value && toValue(configId) > 0),
  });
}

/**
 * The content of one stored version.
 *
 * Never stale: a stored version is immutable by definition, so re-asking for it can only ever
 * get the same answer back.
 */
export function useConfigVersionContent(
  configId: MaybeRef<number>,
  version: MaybeRef<number | null>,
) {
  const canRead = useCanRead();

  return useQuery<StoredConfigVersion>({
    queryKey: computed(() => [
      ...analysisConfigsQueryKey,
      'version',
      toValue(configId),
      toValue(version),
    ]),
    queryFn: () => analysisConfigsApi.version(toValue(configId), toValue(version) as number),
    enabled: computed(() => canRead.value && toValue(version) !== null),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * The gesture catalogue (HU-38).
 *
 * Long-lived: it is the engine's registry and only moves when the engine is deployed. Its
 * **failure** is what the screens care about — a form whose catalogue is unavailable goes
 * read-only rather than saving against a guess, because a configuration saved without it drops
 * every per-gesture setting it could not resolve.
 *
 * Two retries and no more: the point is to reach the read-only state quickly rather than to leave
 * somebody looking at a spinner while the engine is down.
 *
 * `enabled` for a screen that needs it only sometimes: a review asks for it only while labelling.
 */
export function useGestureCatalogue(enabled: MaybeRef<boolean> = true) {
  const canRead = useCanRead();

  return useQuery<GestureCatalogue>({
    queryKey: gestureCatalogueQueryKey,
    queryFn: () => analysisConfigsApi.gestureCatalogue(),
    enabled: computed(() => canRead.value && toValue(enabled)),
    staleTime: 30 * 60_000,
    retry: 2,
  });
}

export function useRulesetCatalogue() {
  const canRead = useCanRead();

  return useQuery<RulesetCatalogue>({
    queryKey: rulesetCatalogueQueryKey,
    queryFn: () => analysisConfigsApi.rulesets(),
    enabled: canRead,
    staleTime: 30 * 60_000,
    retry: 2,
  });
}

function useInvalidateConfigs(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({ queryKey: analysisConfigsQueryKey });
  };
}

export function useCreateAnalysisConfig() {
  const invalidate = useInvalidateConfigs();

  return useMutation({
    mutationFn: (input: SaveConfigInput) => analysisConfigsApi.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateAnalysisConfig() {
  const invalidate = useInvalidateConfigs();

  return useMutation({
    mutationFn: ({ configId, input }: { configId: number; input: SaveConfigInput }) =>
      analysisConfigsApi.update(configId, input),
    onSuccess: invalidate,
  });
}

/**
 * Creates a configuration from «Estándar» or as a copy of another.
 *
 * The answer is the configuration as stored, and it is written into the detail's cache: the screen
 * that opens next is that detail, and it paints at once instead of behind a spinner while it asks
 * again for what it was just handed.
 */
export function useCreateConfigFromBase() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateConfigs();

  return useMutation({
    mutationFn: (input: CreateConfigFromBaseInput) => analysisConfigsApi.createFromBase(input),
    onSuccess: async (created) => {
      queryClient.setQueryData(configDetailKey(created.id), created);
      await invalidate();
    },
  });
}

/** The executions of every configuration: what deleting one execution invalidates. */
export const configExecutionsQueryKey = [...analysisConfigsQueryKey, 'executions'] as const;

export function configExecutionsKey(configId: MaybeRef<number>): readonly unknown[] {
  return [...configExecutionsQueryKey, toValue(configId)];
}

/**
 * The executions run with a configuration, each with its measured figures.
 *
 * Under the configurations' key, so saving or deleting one refreshes it too. Not live: an
 * execution's figures appear when its batch is measured, and the groups screen is where a running
 * batch is watched.
 */
export function useConfigExecutions(configId: MaybeRef<number>) {
  const canRead = useCanRead();

  return useQuery<ConfigExecution[]>({
    queryKey: computed(() => configExecutionsKey(configId)),
    queryFn: () => analysisConfigsApi.executions(toValue(configId)),
    enabled: computed(() => canRead.value && toValue(configId) > 0),
  });
}

/**
 * Makes a configuration the active one.
 *
 * Everything under the configurations' key is refreshed: the list's stars, both details — the one
 * that got the star and the one that lost it — and the active read. Awaited, so the button that
 * asked is not released before the star it lit is on screen.
 */
export function useActivateAnalysisConfig() {
  const invalidate = useInvalidateConfigs();

  return useMutation({
    mutationFn: (configId: number) => analysisConfigsApi.activate(configId),
    onSuccess: invalidate,
  });
}

/**
 * Deletes a configuration.
 *
 * The refusal is the interesting outcome: the API answers `VP_CONFIG_IN_USE` with how many runs
 * and groups hold it, and that sentence is what the screen shows. It is the only guard there is —
 * Virtual Profiler deletes the row without checking.
 */
export function useDeleteAnalysisConfig() {
  const invalidate = useInvalidateConfigs();

  return useMutation({
    mutationFn: (configId: number) => analysisConfigsApi.remove(configId),
    onSuccess: invalidate,
  });
}

/**
 * Deletes several configurations, and answers what happened to each rather than failing whole.
 *
 * There is no bulk endpoint, and the calls go **one after another**: each makes the gateway count
 * every run and group holding that configuration, which is the costly part. A refusal is an
 * outcome, not an error — «2 en uso» is the rule doing its job — so the mutation resolves with the
 * classification and never rejects for one configuration. The list is refreshed once at the end,
 * not once per configuration: fifty refetches of the same list would each show a half-deleted one.
 */
export function useDeleteAnalysisConfigs() {
  const invalidate = useInvalidateConfigs();

  return useMutation({
    mutationFn: (configIds: readonly number[]) =>
      deleteConfigsInTurn(configIds, (configId) => analysisConfigsApi.remove(configId)),
    onSettled: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Analysis groups (HU-19, HU-24 to HU-32)
// ---------------------------------------------------------------------------

export function groupsPageKey(params: ListParams, filters: GroupFilters): readonly unknown[] {
  return [...groupsQueryKey, 'page', params, filters];
}

export function groupQueryKey(groupId: number): readonly unknown[] {
  return [...groupsQueryKey, 'one', groupId];
}

/**
 * Everything a group and a control share — members, executions, measurements, metrics — is keyed by
 * its owner (`lib/group-owner.ts`): `g:12` or `c:gestures`, under the groups either way, so what
 * invalidates the groups moves a control's too.
 */

/** Keyed by the execution too: the members as one batch left them are a different answer. */
export function groupMembersQueryKey(
  owner: GroupOwner,
  batch: string | null = null,
): readonly unknown[] {
  return [...groupsQueryKey, 'members', ownerKey(owner), batch];
}

export function groupBatchesQueryKey(owner: GroupOwner): readonly unknown[] {
  return [...groupsQueryKey, 'batches', ownerKey(owner)];
}

/**
 * The runs of one execution of a group.
 *
 * Under the run subtree and not the groups': withdrawing or cancelling one of them from its row
 * invalidates the runs, and the list it sits in has to notice.
 */
export function groupBatchRunsQueryKey(owner: GroupOwner, batch: string): readonly unknown[] {
  return [...analysisRunsQueryKey, 'group-batch', ownerKey(owner), batch];
}

export function groupSnapshotsQueryKey(owner: GroupOwner): readonly unknown[] {
  return [...groupsQueryKey, 'snapshots', ownerKey(owner)];
}

export function groupMetricsQueryKey(
  owner: GroupOwner,
  query: { threshold?: number | null; batch?: string | null } = {},
): readonly unknown[] {
  return [
    ...groupsQueryKey,
    'metrics',
    ownerKey(owner),
    query.threshold ?? null,
    query.batch ?? null,
  ];
}

/** One execution's videos. Under the groups: a label corrected or a video taken out moves them. */
export function executionVideosQueryKey(owner: GroupOwner, batch: string): readonly unknown[] {
  return [...groupsQueryKey, 'execution-videos', ownerKey(owner), batch];
}

/**
 * The stored versions of an owner's dataset (2026-10-29). Under the groups: a video added or taken
 * out, or a label written, moves where the dataset stands, and both already invalidate the groups.
 */
export function datasetVersionsQueryKey(owner: GroupOwner): readonly unknown[] {
  return [...groupsQueryKey, 'dataset-versions', ownerKey(owner)];
}

export function datasetVersionQueryKey(owner: GroupOwner, version: number): readonly unknown[] {
  return [...datasetVersionsQueryKey(owner), version];
}

/** A key for an owner not known yet: the query it is on is held back (`enabled`) until it is. */
const NO_OWNER: GroupOwner = { groupId: 0 };

/**
 * The videos of one execution of a group, each as its metrics counted it (`lib/execution-detail.ts`).
 * Asked again while `live` — the execution still runs — so a video that ends shows its result.
 */
export function useExecutionVideos(
  owner: MaybeRef<GroupOwner | null>,
  batch: MaybeRef<string>,
  options: MaybeRef<{ enabled?: boolean; live?: boolean }> = {},
) {
  const canRead = useCanRead();

  return useQuery<ExecutionVideos>({
    queryKey: computed(() => executionVideosQueryKey(toValue(owner) ?? NO_OWNER, toValue(batch))),
    queryFn: () => executionVideosApi.list(toValue(owner) ?? NO_OWNER, toValue(batch)),
    enabled: computed(
      () =>
        canRead.value &&
        toValue(owner) !== null &&
        toValue(batch) !== '' &&
        toValue(options).enabled !== false,
    ),
    refetchInterval: () => refetchEvery(DATASET_REFETCH_MS, toValue(options).live === true),
    refetchIntervalInBackground: false,
  });
}

/**
 * The four controls. Under the groups on purpose: launching or changing a control's videos moves
 * its checks, readiness and figures, and every one of those already invalidates the groups — a
 * control's own resources hang there too, keyed `c:<kind>`.
 */
export const controlGroupsQueryKey = [...groupsQueryKey, 'control'] as const;

export const controlGroupsListKey = [...controlGroupsQueryKey, 'list'] as const;

export function controlGroupQueryKey(kind: ControlKind): readonly unknown[] {
  return [...controlGroupsQueryKey, 'one', kind];
}

/** Its videos that are not ready: they move with every label, so they hang under it too. */
export function controlPendingQueryKey(kind: ControlKind): readonly unknown[] {
  return [...controlGroupsQueryKey, 'pending', kind];
}

/**
 * Its gesture map of one execution. Under the control groups for the same reason: upstream works it
 * out against the labels as they are now, so a label written moves it, and a label write already
 * invalidates the groups.
 */
export function controlMatrixQueryKey(kind: ControlKind, batch: string | null): readonly unknown[] {
  return [...controlGroupsQueryKey, 'matrix', kind, batch];
}

export function useAnalysisGroups(
  params: MaybeRef<ListParams>,
  filters: MaybeRef<GroupFilters> = {},
) {
  const canRead = useCanRead();

  return useQuery<PageResult<AnalysisGroup>>({
    queryKey: computed(() => groupsPageKey(toValue(params), toValue(filters))),
    queryFn: () => analysisGroupsApi.list(toValue(params), toValue(filters)),
    enabled: canRead,
    placeholderData: (previous) => previous,
    // Only while some group on this page has something in flight or an execution waiting its turn
    // (HU-27). A list of settled groups is history and stops asking.
    refetchInterval: ({ state }) =>
      refetchEvery(DATASET_REFETCH_MS, groupsAreAlive(state.data?.items)),
    refetchIntervalInBackground: false,
  });
}

export function useAnalysisGroup(groupId: MaybeRef<number>) {
  const canRead = useCanRead();

  return useQuery<AnalysisGroup>({
    queryKey: computed(() => groupQueryKey(toValue(groupId))),
    queryFn: () => analysisGroupsApi.get(toValue(groupId)),
    enabled: computed(() => canRead.value && toValue(groupId) > 0),
    refetchInterval: ({ state }) =>
      refetchEvery(DATASET_REFETCH_MS, groupsAreAlive(state.data ? [state.data] : undefined)),
    refetchIntervalInBackground: false,
  });
}

/**
 * The members of a group or a control (`owner`); as one execution left them when `batch` is given.
 * Held back while the owner is null — a page whose address names nothing yet.
 *
 * `enabled: false` holds the request back until the caller knows which execution to ask about,
 * so the screen does not ask twice — once across executions and once for the latest. Asked again
 * while a member of it is still running, which is how its progress moves on screen.
 */
export function useGroupMembers(
  owner: MaybeRef<GroupOwner | null>,
  scope: MaybeRef<{ batch?: string | null; enabled?: boolean }> = {},
) {
  const canRead = useCanRead();

  return useQuery<GroupMember[]>({
    queryKey: computed(() =>
      groupMembersQueryKey(toValue(owner) ?? NO_OWNER, toValue(scope).batch ?? null),
    ),
    queryFn: () =>
      analysisGroupsApi.members(toValue(owner) ?? NO_OWNER, {
        batch: toValue(scope).batch,
      }),
    enabled: computed(
      () => canRead.value && toValue(owner) !== null && toValue(scope).enabled !== false,
    ),
    refetchInterval: ({ state }) =>
      refetchEvery(
        DATASET_REFETCH_MS,
        (state.data ?? []).some((member) => member.resolved.bucket === 'pending'),
      ),
    refetchIntervalInBackground: false,
  });
}

/**
 * One request for a group's executions, read two ways: the history (`useGroupBatches`) and the
 * ones waiting their turn (`useGroupWaitingExecutions`). Both share the key, so the second costs
 * nothing, and every screen that reads the history never sees a waiting execution.
 *
 * Asked again while a batch is not complete — an automatic retry counts — or one waits: the
 * history has to notice an execution end, and the next one start.
 */
function useGroupExecutionList<T>(
  owner: MaybeRef<GroupOwner | null>,
  select: (list: GroupExecutionList) => T,
) {
  const canRead = useCanRead();

  return useQuery<GroupExecutionList, Error, T>({
    queryKey: computed(() => groupBatchesQueryKey(toValue(owner) ?? NO_OWNER)),
    queryFn: () => analysisGroupsApi.batches(toValue(owner) ?? NO_OWNER),
    enabled: computed(() => canRead.value && toValue(owner) !== null),
    select,
    refetchInterval: ({ state }) =>
      refetchEvery(DATASET_REFETCH_MS, groupExecutionsAreAlive(state.data)),
    refetchIntervalInBackground: false,
  });
}

const historyOf = (list: GroupExecutionList): GroupBatch[] => list.items;
const waitingOf = (list: GroupExecutionList): GroupWaitingExecution[] => list.waiting;

/** The executions of a group or a control that ran, or run: never one waiting its turn. */
export function useGroupBatches(owner: MaybeRef<GroupOwner | null>) {
  return useGroupExecutionList(owner, historyOf);
}

/** The executions of a group or a control waiting their turn, next first. */
export function useGroupWaitingExecutions(owner: MaybeRef<GroupOwner | null>) {
  return useGroupExecutionList(owner, waitingOf);
}

/**
 * The stored versions of an owner's dataset and where it stands now (product owner, 2026-10-29).
 * Held back while the owner is null — the whole dataset has no versions. Asked again while videos
 * are still to label: the moment the last one is labelled is when the screen offers a version.
 */
export function useDatasetVersions(owner: MaybeRef<GroupOwner | null>) {
  const canRead = useCanRead();

  return useQuery<DatasetVersionList>({
    queryKey: computed(() => datasetVersionsQueryKey(toValue(owner) ?? NO_OWNER)),
    queryFn: () => analysisGroupsApi.datasetVersions(toValue(owner) ?? NO_OWNER),
    enabled: computed(() => canRead.value && toValue(owner) !== null),
    refetchInterval: ({ state }) =>
      refetchEvery(DATASET_REFETCH_MS, (state.data?.current.pending ?? 0) > 0),
    refetchIntervalInBackground: false,
  });
}

/** One stored version with its videos. Held back without an owner or a version (null). */
export function useDatasetVersion(
  owner: MaybeRef<GroupOwner | null>,
  version: MaybeRef<number | null>,
) {
  const canRead = useCanRead();

  return useQuery<DatasetVersionDetail>({
    queryKey: computed(() =>
      datasetVersionQueryKey(toValue(owner) ?? NO_OWNER, toValue(version) ?? 0),
    ),
    queryFn: () =>
      analysisGroupsApi.datasetVersion(toValue(owner) ?? NO_OWNER, toValue(version) ?? 0),
    enabled: computed(() => canRead.value && toValue(owner) !== null && toValue(version) !== null),
    // A stored version never changes: once read, it is read.
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * The versions of an owner's dataset, and the groups, read again: after a version is stored, or
 * after one could not be — the screen then shows where the dataset stands and offers the button.
 */
function useInvalidateDatasetVersions(): (owner: GroupOwner) => Promise<void> {
  const queryClient = useQueryClient();

  return async (owner) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: datasetVersionsQueryKey(owner) }),
      queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
    ]);
  };
}

/**
 * Stores the owner's dataset as it is now. The versions and the groups are read again either way:
 * a refusal as unchanged means somebody versioned it meanwhile, and the list has to show that one.
 */
export function useCreateDatasetVersion() {
  const invalidate = useInvalidateDatasetVersions();

  return useMutation({
    mutationFn: ({ owner, note }: { owner: GroupOwner; note?: string | null }) =>
      analysisGroupsApi.createDatasetVersion(owner, { note: note ?? null }),
    onSettled: (_result, _error, { owner }) => invalidate(owner),
  });
}

/**
 * What a background upload refreshes once it closed an owner's bulk with one dataset version
 * (product owner, 2026-10-29), or failed to: the versions, so the select shows the new one — or,
 * after a failure, the screen lights «Generar versión» up — and the groups. For the upload store,
 * which knows nothing about caching, as `useRefreshAfterRegister` is.
 */
export function useRefreshAfterDatasetClose(): (owner: GroupOwner) => Promise<void> {
  return useInvalidateDatasetVersions();
}

/**
 * Keeps a group's screen asking while one of its failures waits for upstream's automatic retry.
 *
 * Nothing else would. Once every run of the latest execution is final, the history, the members
 * and the runs all stop polling — and the retry can be queued after that: the sweep that catches
 * the failures the worker writes straight to the database runs every two minutes. Unseen, it would
 * leave the KPIs on an execution that is about to change until somebody reloaded. Paused while the
 * tab is hidden, like every other poll here.
 */
export function useAwaitGroupRetry(expecting: MaybeRef<boolean>): void {
  const queryClient = useQueryClient();
  let timer: ReturnType<typeof setInterval> | null = null;

  const stop = (): void => {
    if (timer) clearInterval(timer);
    timer = null;
  };

  watch(
    () => toValue(expecting),
    (on) => {
      stop();
      if (!on) return;

      timer = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;

        void queryClient.invalidateQueries({ queryKey: groupsQueryKey });
        void queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey });
      }, DATASET_REFETCH_MS);
    },
    { immediate: true },
  );

  onScopeDispose(stop);
}

/**
 * The measurements of a group.
 *
 * Append-only, so once read they do not change — but a finishing batch adds one, which is why
 * this is invalidated by a launch rather than cached for long.
 */
export function useGroupSnapshots(owner: MaybeRef<GroupOwner | null>) {
  const canRead = useCanRead();

  return useQuery<MetricsSnapshot[]>({
    queryKey: computed(() => groupSnapshotsQueryKey(toValue(owner) ?? NO_OWNER)),
    queryFn: () => analysisGroupsApi.snapshots(toValue(owner) ?? NO_OWNER),
    enabled: computed(() => canRead.value && toValue(owner) !== null),
  });
}

/**
 * The metrics of a group; of one execution when `batch` is given.
 *
 * `enabled: false` until there is something to measure: an execution still running has no
 * numbers, and asking for them would only fetch a half-measured batch.
 */
export function useGroupMetrics(
  owner: MaybeRef<GroupOwner | null>,
  query: MaybeRef<{
    threshold?: number | null;
    batch?: string | null;
    enabled?: boolean;
    /**
     * Keep showing the figures of the same execution while another threshold is asked: moving the
     * threshold must not blank the card on every stop. Never across executions — those would be
     * another execution's figures under this one's name.
     */
    keepWhileRefetching?: boolean;
  }> = {},
) {
  const canRead = useCanRead();

  return useQuery<GroupMetrics>({
    queryKey: computed(() => groupMetricsQueryKey(toValue(owner) ?? NO_OWNER, toValue(query))),
    queryFn: () =>
      analysisGroupsApi.metrics(toValue(owner) ?? NO_OWNER, {
        threshold: toValue(query).threshold,
        batch: toValue(query).batch,
      }),
    enabled: computed(
      () => canRead.value && toValue(owner) !== null && toValue(query).enabled !== false,
    ),
    placeholderData: (previous) =>
      toValue(query).keepWhileRefetching === true &&
      previous !== undefined &&
      previous.batch === (toValue(query).batch ?? null)
        ? previous
        : undefined,
  });
}

/** Which groups the videos of a page belong to. One request, keyed by the ids asked about. */
export function useVideoMemberships(videoIds: MaybeRef<readonly number[]>) {
  const canRead = useCanRead();

  return useQuery<Map<number, GroupMembershipRef[]>>({
    queryKey: computed(() => [...groupsQueryKey, 'membership', [...toValue(videoIds)].sort()]),
    queryFn: () => analysisGroupsApi.membership(toValue(videoIds)),
    enabled: computed(() => canRead.value && toValue(videoIds).length > 0),
    // Membership changes when somebody edits a group, not on its own.
    staleTime: 60_000,
  });
}

/**
 * Invalidates everything about groups.
 *
 * Wholesale rather than surgical: launching a re-analysis changes the group's counts, its
 * batches, its members' resolutions and, once it finishes, its snapshots. Guessing which of
 * those to invalidate is how a screen ends up showing one of them stale.
 */
function useInvalidateGroups(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({ queryKey: groupsQueryKey });
  };
}

export function useCreateGroup() {
  const invalidate = useInvalidateGroups();

  return useMutation({
    mutationFn: (draft: CreateGroupDraft) => analysisGroupsApi.create(draft),
    onSuccess: invalidate,
  });
}

export function useUpdateGroup() {
  const invalidate = useInvalidateGroups();

  return useMutation({
    mutationFn: ({ groupId, draft }: { groupId: number; draft: UpdateGroupDraft }) =>
      analysisGroupsApi.update(groupId, draft),
    onSuccess: invalidate,
  });
}

export function useDeleteGroup() {
  const invalidate = useInvalidateGroups();

  return useMutation({
    mutationFn: (groupId: number) => analysisGroupsApi.remove(groupId),
    onSuccess: invalidate,
  });
}

/**
 * What a change of a group's videos moves (product owner, 2026-10-06): the gallery — its pages by
 * group, and the «Añadir vídeos» list, which leaves the members out — and every read of the groups:
 * the group and its members, the list with its counts and «Reanalizar», and the control groups'
 * readiness, which hang under the groups.
 */
export const MEMBERSHIP_QUERY_KEYS = [videosQueryKey, groupsQueryKey] as const;

function useInvalidateMembership(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all(
      MEMBERSHIP_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  };
}

export function useAddGroupMembers() {
  const invalidate = useInvalidateMembership();

  return useMutation({
    mutationFn: ({ owner, videoIds }: { owner: GroupOwner; videoIds: readonly number[] }) =>
      analysisGroupsApi.addMembers(owner, videoIds),
    onSuccess: invalidate,
  });
}

export function useRemoveGroupMember() {
  const invalidate = useInvalidateMembership();

  return useMutation({
    mutationFn: ({ owner, memberId }: { owner: GroupOwner; memberId: number }) =>
      analysisGroupsApi.removeMember(owner, memberId),
    onSuccess: invalidate,
  });
}

/**
 * Several videos out of a group in one call (`removeAll`, 2026-10-29: one dataset version for all
 * of them), and the group read again once at the end. Resolves with the tally, never
 * rejects: a failure is one video's outcome.
 */
export function useRemoveGroupMembers() {
  const invalidate = useInvalidateMembership();

  return useMutation({
    mutationFn: ({ owner, memberIds }: { owner: GroupOwner; memberIds: readonly number[] }) =>
      removeAll(memberIds, (ids) => analysisGroupsApi.removeMembers(owner, ids)),
    onSettled: invalidate,
  });
}

export function usePinGroupMember() {
  const invalidate = useInvalidateGroups();

  return useMutation({
    mutationFn: ({
      owner,
      memberId,
      runId,
    }: {
      owner: GroupOwner;
      memberId: number;
      runId: string | null;
    }) => analysisGroupsApi.pinMember(owner, memberId, runId),
    onSuccess: invalidate,
  });
}

/**
 * Launches a new execution of a group with the parameters chosen for it: each launch is its own
 * execution, and records them.
 *
 * Invalidates the queue as well as the groups: the runs it just launched are what the queue
 * screen is about, and somebody who launches from here looks there next. And the gallery's live
 * runs, since a group re-analysis never touches a video's row and those are how a card shows it.
 * The reviews are left alone — a group run never promotes, so nothing any of them shows changes.
 */
export function useRunGroup() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateGroups();
  const subscriber = useExecutionSubscriber();
  const refresh = async () => {
    await Promise.all([
      invalidate(),
      queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey }),
      queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
      queryClient.invalidateQueries({ queryKey: videosQueryKey }),
    ]);
  };

  return useMutation({
    /** `notify`: email me when it finishes, asked once the launch has its label (`launchWithEmail`). */
    mutationFn: ({
      groupId,
      parameters,
      notify = false,
    }: {
      groupId: number;
      parameters: GroupLaunchParameters;
      notify?: boolean;
    }) =>
      launchWithEmail(
        () => analysisGroupsApi.run(groupId, parameters),
        notify ? subscriber(groupOwner(groupId)) : null,
      ),
    onSuccess: refresh,
    // Upstream goes on launching members after we stop waiting: some of them may be queued.
    onError: async (error) => {
      if (mayHaveLaunched(error)) await refresh();
    },
  });
}

/**
 * The three control groups, with their checks, readiness and figures (product owner, 2026-09-28;
 * static since 2026-10-06).
 *
 * `live` while one of their groups has something in flight: its latest fitting execution, and so
 * its figures, can change by itself then. A settled board stops asking.
 */
export function useControlGroups(live: MaybeRef<boolean> = false) {
  const canRead = useCanRead();

  return useQuery<ControlGroup[]>({
    queryKey: controlGroupsListKey,
    queryFn: () => controlGroupsApi.list(),
    enabled: canRead,
    refetchInterval: () => refetchEvery(DATASET_REFETCH_MS, toValue(live)),
    refetchIntervalInBackground: false,
  });
}

/**
 * One control group by its kind, asked again as the list is while `live`. Held back while not
 * `enabled`: the control's page asks nothing for an address that names no kind.
 */
export function useControlGroup(
  kind: MaybeRef<ControlKind>,
  live: MaybeRef<boolean> = false,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<ControlGroup>({
    queryKey: computed(() => controlGroupQueryKey(toValue(kind))),
    queryFn: () => controlGroupsApi.get(toValue(kind)),
    enabled: computed(() => canRead.value && toValue(enabled)),
    refetchInterval: () => refetchEvery(DATASET_REFETCH_MS, toValue(live)),
    refetchIntervalInBackground: false,
  });
}

/**
 * The videos of a control group that lack something its kind measures with. Held back until
 * somebody looks (`enabled`): the card already counts them, and the list is only for whoever wants
 * to know which.
 */
export function useControlPending(kind: MaybeRef<ControlKind>, enabled: MaybeRef<boolean> = true) {
  const canRead = useCanRead();

  return useQuery<ControlPendingVideo[]>({
    queryKey: computed(() => controlPendingQueryKey(toValue(kind))),
    queryFn: () => controlGroupsApi.pending(toValue(kind)),
    enabled: computed(() => canRead.value && toValue(enabled)),
  });
}

/**
 * A control group's gesture map of one execution (`batch`; null is the kind's latest). Only Gestos
 * and Semántica have one: held back for Coherencia, and until somebody looks (`enabled`). No
 * placeholder from the execution before: those would be another execution's videos under this
 * one's name.
 */
export function useControlGestureMatrix(
  kind: MaybeRef<ControlKind>,
  batch: MaybeRef<string | null>,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<GestureMatrix>({
    queryKey: computed(() => controlMatrixQueryKey(toValue(kind), toValue(batch))),
    // `enabled` keeps Coherencia from ever being asked: the kind here is one with a map.
    queryFn: () => controlGroupsApi.matrix(toValue(kind) as GestureMatrixKind, toValue(batch)),
    enabled: computed(() => canRead.value && toValue(kind) !== 'coherence' && toValue(enabled)),
  });
}

/**
 * The gesture detection totals of each execution given, by label: one gesture map per execution,
 * the same request (and cache) as the «Mapa de gestos» tab. Only the executions that fit Gestos are
 * worth asking (`gestureMeasuredBatches`); `pending` holds the labels still on their way.
 */
export function useGestureExecutionTotals(batches: MaybeRef<readonly string[]>) {
  const canRead = useCanRead();

  const queries = useQueries({
    queries: computed(() =>
      toValue(batches).map((batch) => ({
        queryKey: controlMatrixQueryKey('gestures', batch),
        queryFn: () => controlGroupsApi.matrix('gestures', batch),
        enabled: canRead.value,
        staleTime: 60_000,
      })),
    ),
  });

  const totals = computed(() => {
    const byBatch = new Map<string, GestureControlMetrics>();
    for (const query of queries.value) {
      if (query.data?.batch) byBatch.set(query.data.batch, query.data.totals);
    }
    return byBatch;
  });

  const pending = computed(() => {
    const labels = toValue(batches);
    return new Set(labels.filter((_, index) => queries.value[index]?.isPending));
  });

  return { totals, pending };
}

/**
 * Launches an execution of a control, with the engines and the gesture source its kind sets
 * upstream. Refreshes what a group run refreshes, for the same reasons — the controls among them,
 * under the groups. The email, when asked, is the control's: its link goes to the control's page.
 */
export function useRunControl() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateGroups();
  const subscriber = useExecutionSubscriber();
  const refresh = async () => {
    await Promise.all([
      invalidate(),
      queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey }),
      queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
      queryClient.invalidateQueries({ queryKey: videosQueryKey }),
    ]);
  };

  return useMutation({
    mutationFn: ({
      kind,
      parameters,
      notify = false,
    }: {
      kind: ControlKind;
      parameters: ControlLaunchParameters;
      notify?: boolean;
    }) =>
      launchWithEmail(
        () => controlGroupsApi.run(kind, parameters),
        notify ? subscriber(controlOwner(kind)) : null,
      ),
    onSuccess: refresh,
    // Upstream goes on launching members after we stop waiting: some of them may be queued.
    onError: async (error) => {
      if (mayHaveLaunched(error)) await refresh();
    },
  });
}

/**
 * Asks for the email of one owner's executions, linking to where this app shows each: the group, or
 * the control, on that execution, by its address in the router — so a route that moves takes the
 * link with it.
 */
export function useExecutionSubscriber(): (
  owner: GroupOwner,
) => (batch: string) => Promise<ExecutionSubscription> {
  const router = useRouter();

  return (owner) => (batch) =>
    analysisGroupsApi.subscribeToExecution(
      owner,
      batch,
      new URL(
        router.resolve(executionResultLocation(owner, batch)).href,
        window.location.origin,
      ).toString(),
    );
}

/**
 * Cancels one execution of a group, or takes it out of the queue — its runs, or the whole execution
 * while it waits its turn: the same call upstream.
 *
 * Invalidates the groups, the runs and the queue once it settled — an error included, since part
 * of the execution may have been withdrawn before it: the execution's badge, its runs and the
 * queue screen all change. Nothing finished is touched, so the reviews are left alone.
 */
export function useCancelGroupBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ owner, batch }: { owner: GroupOwner; batch: string }) =>
      analysisGroupsApi.cancelBatch(owner, batch),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey }),
      ]);
    },
  });
}

/**
 * Pauses one execution of a group: its runs that had not started wait, and the running ones finish.
 *
 * Invalidates what the cancel does, once it settled — a refusal included: it means the execution
 * moved under the screen, and the screen has to catch up.
 */
export function usePauseGroupBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ owner, batch }: { owner: GroupOwner; batch: string }) =>
      analysisGroupsApi.pauseBatch(owner, batch),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey }),
      ]);
    },
  });
}

/** Resumes a paused execution: back to its group's queue of executions. Invalidates as the pause. */
export function useResumeGroupBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ owner, batch }: { owner: GroupOwner; batch: string }) =>
      analysisGroupsApi.resumeBatch(owner, batch),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey }),
      ]);
    },
  });
}

/**
 * Deletes one execution of a group with its runs, their results and its figures.
 *
 * Invalidates more than the cancel, because more is gone: besides the groups — history, figures,
 * series and control groups are under them — the runs and the queue, the global metrics, the videos
 * and the reviews, any of which may have been reading one of its runs, and the executions of its
 * configuration, which list it too.
 */
export function useDeleteGroupBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ owner, batch }: { owner: GroupOwner; batch: string }) =>
      analysisGroupsApi.deleteBatch(owner, batch),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisQueueQueryKey }),
        queryClient.invalidateQueries({ queryKey: metricsQueryKey }),
        queryClient.invalidateQueries({ queryKey: videosQueryKey }),
        queryClient.invalidateQueries({ queryKey: analysisDetailQueryKey }),
        queryClient.invalidateQueries({ queryKey: configExecutionsQueryKey }),
      ]);
    },
  });
}

/** The most runs one execution's row lists: the gateway's largest page. */
export const GROUP_BATCH_RUNS_LIMIT = 200;

/**
 * The runs of one execution of a group, for its expanded row.
 *
 * Held back until the row is open (`enabled`), and asked again while any of them can still move,
 * at the groups' cadence; it stops by itself once they are all final. The first
 * `GROUP_BATCH_RUNS_LIMIT` only — the row says when there are more.
 */
export function useGroupBatchRuns(
  owner: MaybeRef<GroupOwner | null>,
  batch: MaybeRef<string>,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<PageResult<AnalysisRun>>({
    queryKey: computed(() => groupBatchRunsQueryKey(toValue(owner) ?? NO_OWNER, toValue(batch))),
    queryFn: () =>
      analysisGroupsApi.batchRuns(toValue(owner) ?? NO_OWNER, toValue(batch), {
        page: 1,
        pageSize: GROUP_BATCH_RUNS_LIMIT,
      }),
    enabled: computed(
      () => canRead.value && toValue(owner) !== null && Boolean(toValue(batch)) && toValue(enabled),
    ),
    refetchInterval: ({ state }) =>
      refetchEvery(DATASET_REFETCH_MS, runsAreAlive(state.data?.items)),
    refetchIntervalInBackground: false,
  });
}

/**
 * Compares two re-analyses of the same group.
 *
 * A mutation although it reads: the pair is chosen by hand and changes on every click, so
 * caching one entry per pair would fill the cache with comparisons nobody asks twice.
 */
export function useCompareBatches() {
  return useMutation({
    mutationFn: ({ owner, a, b }: { owner: GroupOwner; a: string; b: string }) =>
      analysisGroupsApi.compareBatches(owner, a, b),
  });
}

export function useCompareGroups() {
  return useMutation({
    mutationFn: ({ a, b, commonOnly }: { a: number; b: number; commonOnly?: boolean }) =>
      analysisGroupsApi.compare(a, b, commonOnly ?? true),
  });
}

/**
 * What changed in the instrument between two batches of a group: the configuration and the
 * engine versions.
 *
 * Three reads, and the orchestration is the reason this is not a single endpoint: the sealed
 * configuration only travels on `GET /analysis-runs/:id`, so the batches have to be resolved to a
 * run each and those runs read individually. Every list projects the snapshot out, because it
 * carries the whole configuration document. The engine versions come from the same two runs: the
 * batch history does not carry them, and the versions a run actually ran with are the ones that
 * matter.
 *
 * Any run of a batch will do for the configuration: every run of one batch was launched with the
 * same one — that is what a batch is. A run that succeeded is preferred, because a failed one may
 * never have reached the engines and has no versions to compare.
 */
export function useBatchConfigDiff() {
  return useMutation({
    mutationFn: async ({
      owner,
      a,
      b,
    }: {
      owner: GroupOwner;
      a: string;
      b: string;
    }): Promise<{ config: ConfigChange[]; engines: EngineChange[] }> => {
      const groupId = ownerGroupId(owner);
      const controlKind = ownerControlKind(owner);
      const page = await analysisRunsApi.history(
        { page: 1, pageSize: 200 },
        // The owner's runs and nobody else's: a control's are filtered by its kind.
        groupId !== null ? { groupId: String(groupId) } : { controlKind: controlKind ?? undefined },
      );

      const runOf = (batch: string) =>
        page.items.find((run) => run.batch === batch && run.status === 'SUCCEEDED') ??
        page.items.find((run) => run.batch === batch);
      const before = runOf(a);
      const after = runOf(b);

      if (!before || !after) return { config: [], engines: [] };

      const [beforeRun, afterRun] = await Promise.all([
        analysisRunsApi.get(before.id),
        analysisRunsApi.get(after.id),
      ]);

      return {
        config: diffConfigs(beforeRun.config?.snapshot ?? null, afterRun.config?.snapshot ?? null),
        engines: diffEngineVersions(beforeRun.engine?.engines, afterRun.engine?.engines),
      };
    },
  });
}

/**
 * The coherence metrics, for one scope.
 *
 * Keyed by the scope, which is what keeps two scopes' numbers from ever appearing together:
 * switching from global to a group changes the key, so the screen gets that scope's answer or a
 * loading state — never the previous one relabelled.
 */
export function useCoherenceMetrics(
  scope: MaybeRef<CoherenceScope> = {},
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<CoherenceMetrics>({
    queryKey: computed(() => [...metricsQueryKey, toValue(scope)]),
    queryFn: () => metricsApi.read(toValue(scope)),
    enabled: computed(() => canRead.value && toValue(enabled)),
    // No placeholder from the previous scope, deliberately: it would show one group's numbers
    // under another group's heading for as long as the request takes.
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// One analysis of one video (HU-33 to HU-36)
// ---------------------------------------------------------------------------

/**
 * The key of one part of one review. Exported for its spec: the launch mutations invalidate by its
 * prefix and read its run segment, and a reshuffle here would make them miss without a sound.
 */
export function analysisKey(
  videoId: number,
  runId: string | undefined,
  part: string,
): readonly unknown[] {
  // The run is part of the key: showing a different run must fetch, not reuse the active one's.
  return [...analysisDetailQueryKey, part, videoId, runId ?? 'active'];
}

export function videoLabellingQueryKey(videoId: number): readonly unknown[] {
  return [...analysisDetailQueryKey, 'labelling', videoId];
}

/**
 * A video's own transcription (product owner, 2026-09-28): per video like the labels, and not one of
 * `ANALYSIS_PARTS` — it is made once by a job of its own, so a relaunch or a run landing changes
 * nothing about it. Not the analysis part `transcription` either, which is a run's plain text.
 */
export function videoTranscriptionQueryKey(videoId: number): readonly unknown[] {
  return [...analysisDetailQueryKey, 'video-transcription', videoId];
}

/**
 * The gestures a person labelled on a video: per video, like the reference truth, and not one of
 * `ANALYSIS_PARTS` — a relaunch or a run landing changes nothing about them, so those invalidations
 * must never reach this key. One key per scope: the video's gestures and its alerts are two
 * documents (2026-10-29), and a write to one must not show up as the other.
 */
export function gestureLabelsQueryKey(
  videoId: number,
  scope: GestureLabelScope = 'gestures',
): readonly unknown[] {
  return [...analysisDetailQueryKey, 'gesture-labels', scope, videoId];
}

/**
 * The header of a review: the video URL and which run produced what is shown.
 *
 * `staleTime: 0` and no long cache, because the signed URL dies in two hours and there is no
 * renewal endpoint. A cached answer would hand the player a dead URL and look like a broken video.
 */
export function useAnalysisMetadata(
  videoId: MaybeRef<number>,
  runId: MaybeRef<string | undefined>,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<AnalysisMetadata>({
    queryKey: computed(() => analysisKey(toValue(videoId), toValue(runId), 'metadata')),
    queryFn: () => analysisDetailApi.metadata(toValue(videoId), toValue(runId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0 && toValue(enabled)),
    staleTime: 0,
  });
}

/**
 * The gestures of one analysis. Megabytes, so it is cached hard.
 *
 * An analysis is immutable once it finished — that is the whole point of the run ledger — so there
 * is nothing to re-ask for. Only a different run is a different answer, and the key says so.
 */
export function useGestureAnalysis(
  videoId: MaybeRef<number>,
  runId: MaybeRef<string | undefined>,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<GestureAnalysis>({
    queryKey: computed(() => analysisKey(toValue(videoId), toValue(runId), 'gestures')),
    queryFn: () => analysisDetailApi.gestures(toValue(videoId), toValue(runId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0 && toValue(enabled)),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useTranscription(
  videoId: MaybeRef<number>,
  runId: MaybeRef<string | undefined>,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<Transcription>({
    queryKey: computed(() => analysisKey(toValue(videoId), toValue(runId), 'transcription')),
    queryFn: () => analysisDetailApi.transcription(toValue(videoId), toValue(runId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0 && toValue(enabled)),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * A window of landmark frames around the playhead.
 *
 * Keyed by the window, so scrubbing through a video builds up the slices it has visited and
 * revisiting one costs nothing. Immutable for the same reason as the gestures.
 */
export function useLandmarks(
  videoId: MaybeRef<number>,
  runId: MaybeRef<string | undefined>,
  window: MaybeRef<{ startSeconds: number; endSeconds: number }>,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<LandmarkWindow>({
    queryKey: computed(() => [
      ...analysisKey(toValue(videoId), toValue(runId), 'landmarks'),
      toValue(window),
    ]),
    queryFn: () => analysisDetailApi.landmarks(toValue(videoId), toValue(window), toValue(runId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0 && toValue(enabled)),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Refreshes a review when one of its video's runs lands (GAP-09).
 *
 * The review screen has no socket, so the gallery's live-runs query is the signal: a run of this
 * video that was in flight and no longer is has finished, one way or another. It keeps being asked
 * slowly while nothing is in flight, or a run launched from another screen would never be seen.
 *
 * How much is asked again depends on whether the pointer can have moved. A run of the video's own
 * launched to promote can make another run «la activa» and rewrites the transcription: then every
 * header of the video is asked again — a review opened on an explicit run included, which would
 * otherwise keep its «Activa» chip and offer a «Fijar» that silently promotes the old run back —
 * with the transcription and whatever was cached under «la activa». That reloads the player,
 * deliberately, because what it shows is no longer what the video shows. A group run or one
 * launched without promotion changes nothing any review shows but its own header, which is the
 * only thing asked again, for somebody who was watching it run.
 */
export function useRefreshWhenRunsLand(videoId: MaybeRef<number>): void {
  const queryClient = useQueryClient();
  const live = useLiveVideoRuns({ idleEveryMs: REVIEW_IDLE_REFETCH_MS });

  watch(
    () => live.data.value?.items ?? [],
    (current, previous) => {
      const id = toValue(videoId);
      const landed = runsThatLanded(previous ?? [], current, id);

      if (landed.length === 0) return;

      const part = (name: string, ...rest: unknown[]) =>
        queryClient.invalidateQueries({
          queryKey: [...analysisDetailQueryKey, name, id, ...rest],
        });

      const parts = landingMayHavePromoted(previous ?? [], landed)
        ? [
            part('metadata'),
            part('gestures', 'active'),
            part('landmarks', 'active'),
            part('transcription'),
          ]
        : landed.map((runId) => part('metadata', runId));

      void Promise.all([
        ...parts,
        queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey }),
        queryClient.invalidateQueries({ queryKey: videoQueryKey(id) }),
      ]);
    },
  );
}

/**
 * A video's truth labelling: its tramos de mentira and whether it is complete. A video nobody
 * labelled reads `unlabelled`, never «Verdad».
 */
export function useVideoLabelling(videoId: MaybeRef<number>, enabled: MaybeRef<boolean> = true) {
  const canRead = useCanRead();

  return useQuery<VideoLabelling>({
    queryKey: computed(() => videoLabellingQueryKey(toValue(videoId))),
    queryFn: () => videoLabellingApi.get(toValue(videoId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0 && toValue(enabled)),
  });
}

/**
 * After a labelling write: the answer is shown as it is, and what depends on it is asked again.
 *
 * The gallery shows the label on every card, the metrics move with it — a newly labelled video
 * enters every denominator — and the groups and control groups count and list the videos still
 * unlabelled. A write answered without the labelling reads it again instead.
 */
function useAfterLabellingWrite(): (
  videoId: number,
  labelling: VideoLabelling | null,
) => Promise<void> {
  const queryClient = useQueryClient();

  return async (videoId, labelling) => {
    if (labelling) queryClient.setQueryData(videoLabellingQueryKey(videoId), labelling);

    await Promise.all([
      labelling
        ? Promise.resolve()
        : queryClient.invalidateQueries({
            queryKey: videoLabellingQueryKey(videoId),
          }),
      queryClient.invalidateQueries({ queryKey: videosQueryKey }),
      queryClient.invalidateQueries({ queryKey: metricsQueryKey }),
      // The control groups hang under the groups: one invalidation reaches both.
      queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
      queryClient.invalidateQueries({ queryKey: controlGroupsQueryKey }),
    ]);
  };
}

/** Marks a moment as an incoherence: a new range without `rangeId`, a moved one with it. */
export function useSaveIncoherenceRange() {
  const after = useAfterLabellingWrite();

  return useMutation({
    mutationFn: ({
      videoId,
      rangeId,
      range,
    }: {
      videoId: number;
      rangeId: number | null;
      range: IncoherenceRange;
    }) =>
      rangeId === null
        ? videoLabellingApi.addRange(videoId, range)
        : videoLabellingApi.updateRange(videoId, rangeId, range),
    onSuccess: (labelling, { videoId }) => after(videoId, labelling),
  });
}

export function useRemoveIncoherenceRange() {
  const after = useAfterLabellingWrite();

  return useMutation({
    mutationFn: ({ videoId, rangeId }: { videoId: number; rangeId: number }) =>
      videoLabellingApi.removeRange(videoId, rangeId),
    onSuccess: (labelling, { videoId }) => after(videoId, labelling),
  });
}

/** «Verdad» is the labelling marked complete with no tramo de mentira. */
export function useSetLabellingComplete() {
  const after = useAfterLabellingWrite();

  return useMutation({
    mutationFn: ({ videoId, complete }: { videoId: number; complete: boolean }) =>
      videoLabellingApi.setComplete(videoId, complete),
    onSuccess: (labelling, { videoId }) => after(videoId, labelling),
  });
}

/**
 * The whole labelling in one write (2026-10-29): these ranges and this «Completo», replacing what
 * was saved. What a step of the upload's labelling saves, revisited or not.
 */
export function useReplaceLabelling() {
  const after = useAfterLabellingWrite();

  return useMutation({
    mutationFn: ({
      videoId,
      ranges,
      complete,
    }: {
      videoId: number;
      ranges: readonly IncoherenceRange[];
      complete: boolean;
    }) => videoLabellingApi.replace(videoId, ranges, complete),
    onSettled: (labelling, _error, { videoId }) => after(videoId, labelling ?? null),
  });
}

/** Every range and the mark: the video goes back to «Sin etiquetar», out of every metric. */
export function useClearLabelling() {
  const after = useAfterLabellingWrite();

  return useMutation({
    mutationFn: (videoId: number) => videoLabellingApi.clear(videoId),
    onSuccess: (_answer, videoId) => after(videoId, null),
  });
}

/**
 * «Verdad» as one act: the tramos de mentira cleared, when there are any, and the labelling marked
 * complete. Everything is read again once, at the end — never between the two writes, where the
 * video is «Sin etiquetar» for a moment — and also after a failure, which shows what is stored.
 */
export function useMarkTruthful() {
  const after = useAfterLabellingWrite();

  return useMutation({
    mutationFn: ({ videoId, clearFirst }: { videoId: number; clearFirst: boolean }) =>
      videoLabellingApi.markTruthful(videoId, clearFirst),
    onSettled: (labelling, _error, { videoId }) => after(videoId, labelling ?? null),
  });
}

/**
 * A video's own transcription, asked again every few seconds while its job is waiting or running
 * and never once it settled: done, failed or never asked for only change when somebody asks.
 */
export function useVideoTranscription(
  videoId: MaybeRef<number>,
  enabled: MaybeRef<boolean> = true,
) {
  const canRead = useCanRead();

  return useQuery<VideoTranscription>({
    queryKey: computed(() => videoTranscriptionQueryKey(toValue(videoId))),
    queryFn: () => videoTranscriptionsApi.get(toValue(videoId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0 && toValue(enabled)),
    refetchInterval: ({ state }) =>
      refetchEvery(TRANSCRIPTION_REFETCH_MS, transcriptionIsAlive(state.data?.status)),
    refetchIntervalInBackground: false,
  });
}

/**
 * «Transcribir» or «Reintentar»: asks for the video's transcription again.
 *
 * The answer is the document, now pending, written straight into the cache so the panel starts
 * asking again by itself. The gallery's rows carry the status too. A 409 means somebody else asked
 * first or the job finished meanwhile: the document is read again, so the panel catches up.
 */
export function useRetryTranscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (videoId: number) => videoTranscriptionsApi.retry(videoId),
    onSuccess: async (transcription, videoId) => {
      queryClient.setQueryData(videoTranscriptionQueryKey(videoId), transcription);
      await queryClient.invalidateQueries({ queryKey: videosQueryKey });
    },
    onError: async (error, videoId) => {
      if (error instanceof ApiError && error.is(ApiErrorCode.VP_TRANSCRIPTION_BUSY)) {
        await queryClient.invalidateQueries({
          queryKey: videoTranscriptionQueryKey(videoId),
        });
      }
    },
  });
}

/**
 * The gestures a person labelled on a video, and whether its labelling is complete: the gestures
 * unless a scope says otherwise, only the alerts for Alertas gestuales.
 */
export function useGestureLabels(
  videoId: MaybeRef<number>,
  enabled: MaybeRef<boolean> = true,
  scope: MaybeRef<GestureLabelScope> = 'gestures',
) {
  const canRead = useCanRead();

  return useQuery<VideoGestureLabels>({
    queryKey: computed(() => gestureLabelsQueryKey(toValue(videoId), toValue(scope))),
    queryFn: () => gestureLabelsApi.get(toValue(videoId), toValue(scope)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0 && toValue(enabled)),
  });
}

/**
 * After a label write: the answer is shown as it is, and what depends on the labels is asked again.
 *
 * The groups and the control groups count labelled and complete videos per group, and the gallery's
 * row may say so too. A write answered without the document reads the labels again instead.
 */
function useAfterGestureLabelWrite(): (
  videoId: number,
  scope: GestureLabelScope,
  doc: VideoGestureLabels | null,
) => Promise<void> {
  const queryClient = useQueryClient();

  return async (videoId, scope, doc) => {
    if (doc) queryClient.setQueryData(gestureLabelsQueryKey(videoId, scope), doc);

    await Promise.all([
      doc
        ? Promise.resolve()
        : queryClient.invalidateQueries({
            queryKey: gestureLabelsQueryKey(videoId, scope),
          }),
      queryClient.invalidateQueries({ queryKey: videosQueryKey }),
      // The control groups hang under the groups: one invalidation reaches both.
      queryClient.invalidateQueries({ queryKey: groupsQueryKey }),
    ]);
  };
}

/** Writes a label: a new one without `labelId`, a change to that one with it. */
export function useSaveGestureLabel() {
  const after = useAfterGestureLabelWrite();

  return useMutation({
    mutationFn: ({
      videoId,
      scope,
      labelId,
      draft,
    }: {
      videoId: number;
      scope: GestureLabelScope;
      labelId: string | null;
      draft: GestureLabelDraft;
    }) =>
      labelId === null
        ? gestureLabelsApi.create(videoId, draft, scope)
        : gestureLabelsApi.update(videoId, labelId, draft, scope),
    onSuccess: (doc, { videoId, scope }) => after(videoId, scope, doc),
  });
}

export function useDeleteGestureLabel() {
  const after = useAfterGestureLabelWrite();

  return useMutation({
    mutationFn: ({
      videoId,
      scope,
      labelId,
    }: {
      videoId: number;
      scope: GestureLabelScope;
      labelId: string;
    }) => gestureLabelsApi.remove(videoId, labelId, scope),
    onSuccess: (doc, { videoId, scope }) => after(videoId, scope, doc),
  });
}

/** «Etiquetado completo» of one scope's document: what lets the video count for the control groups. */
export function useSetGestureLabellingComplete() {
  const after = useAfterGestureLabelWrite();

  return useMutation({
    mutationFn: ({
      videoId,
      scope,
      complete,
    }: {
      videoId: number;
      scope: GestureLabelScope;
      complete: boolean;
    }) => gestureLabelsApi.setComplete(videoId, complete, scope),
    onSuccess: (doc, { videoId, scope }) => after(videoId, scope, doc),
  });
}

export function feedbackQueryKey(videoId: number, runId: string | undefined): readonly unknown[] {
  return [...analysisDetailQueryKey, 'feedback', videoId, runId ?? 'all'];
}

/** Judgements already written about this analysis, so the screen can show what was reviewed. */
export function useDetectionFeedback(
  videoId: MaybeRef<number>,
  runId: MaybeRef<string | undefined>,
) {
  const canRead = useCanRead();

  return useQuery<DetectionFeedback[]>({
    queryKey: computed(() => feedbackQueryKey(toValue(videoId), toValue(runId))),
    queryFn: () => detectionFeedbackApi.list(toValue(videoId), toValue(runId)),
    enabled: computed(() => canRead.value && toValue(videoId) > 0),
  });
}

/**
 * Records a judgement.
 *
 * Invalidates the feedback of that run only. The gesture metrics computed from this table are a
 * different screen's concern and a different query; invalidating everything would refetch
 * megabytes of analysis for a note about one detection.
 */
export function useCreateFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft: FeedbackDraft) => detectionFeedbackApi.create(draft),
    onSuccess: async (_feedback, draft) => {
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKey(draft.videoId, draft.runId),
      });
    },
  });
}

/**
 * Several judgements of one video and run in one write (2026-10-29): an idea's verdict and the
 * alerts the reviewer marked go together, or none does.
 */
export function useCreateFeedbacks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, drafts }: { videoId: number; runId: string; drafts: FeedbackDraft[] }) =>
      detectionFeedbackApi.createMany(videoId, drafts),
    onSuccess: async (_feedback, { videoId, runId }) => {
      await queryClient.invalidateQueries({ queryKey: feedbackQueryKey(videoId, runId) });
    },
  });
}

/** Takes a judgement back: a «no detectado» reported on the wrong instant, above all. */
export function useRemoveFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { videoId: number; runId: string; feedbackId: number }) =>
      detectionFeedbackApi.remove(input.videoId, input.feedbackId),
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKey(input.videoId, input.runId),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Engine versions
// ---------------------------------------------------------------------------

export const engineChangelogQueryKey = [...virtualProfilerQueryKey, 'engine-changelog'] as const;

/**
 * What changed in each engine, version by version.
 *
 * Text that ships with the engines, so it only moves on a deploy: half an hour fresh, like the
 * other registries that live in the engines' code. The groups board and the changelog page share
 * the one entry, so going from one to the other asks for nothing.
 *
 * An empty list is the exception: it means Virtual Profiler could not read its changelog, not that
 * no engine has one, so it is asked for again on the next screen instead of kept for half an hour.
 */
export function useEngineChangelog() {
  const canRead = useCanRead();

  return useQuery<EngineChangelog[]>({
    queryKey: engineChangelogQueryKey,
    queryFn: () => engineChangelogApi.list(),
    enabled: canRead,
    staleTime: (query) => (query.state.data?.length ? 30 * 60_000 : 0),
  });
}

// ── The session's preferences ────────────────────────────────────────────────

export const preferencesQueryKey = ['session', 'preferences'] as const;

/** The session user's display choices, kept per user on the server (`/session/preferences`). */
export function usePreferences() {
  return useQuery<UserPreferences>({
    queryKey: preferencesQueryKey,
    queryFn: () => preferencesApi.get(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Changes a choice at once on every screen (optimistic) and keeps it on the server; a failure puts
 * the previous choice back, so no screen keeps showing one that was not saved.
 */
export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (change: Partial<UserPreferences>) => preferencesApi.update(change),
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey: preferencesQueryKey });
      const previous = queryClient.getQueryData<UserPreferences>(preferencesQueryKey);
      queryClient.setQueryData<UserPreferences>(preferencesQueryKey, {
        ...(previous ?? {
          globalMetrics: false,
          scoreMethod: DEFAULT_SCORE_METHOD,
          playbackRate: DEFAULT_PLAYBACK_RATE,
        }),
        ...change,
      });

      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous) queryClient.setQueryData(preferencesQueryKey, context.previous);
    },
    onSuccess: (saved) => queryClient.setQueryData(preferencesQueryKey, saved),
  });
}
